import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  listCodebaseRepos,
  syncCodebaseRepos,
  UnifiedRepository,
} from '@/apis/unifiedCodebaseManagement';
import { unifiedCodebaseQueryKeys } from '@/hooks/useUnifiedCodebaseManagement';
import { useToastManager } from '@/hooks/useToastManager';
// orgResolutionDone is read from useUserLogged, not from useUserContext.
// useUserLogged now derives it from the ['serverUser']/['serverOrg'] queries;
// the UserContext field is only written by the two onboarding call sites since
// useUserLogged's auto-fetch effects were removed, so reading it from there
// would leave it false on normal navigation and pin isLoading true forever for
// a user with no workspace -- the exact [FE-33] empty-state bug this gate is
// meant to prevent, inverted.
import { useUserLogged } from '@/hooks/useUserLogged';
import {
  codebaseReposQueryKeys,
  type CodebaseReposFilters,
} from '@/lib/queryKeys/codebaseRepos';

export { codebaseReposQueryKeys, type CodebaseReposFilters };

/**
 * Infinite-scroll listing of the org's repos from the DB (single source of
 * truth — instant, complete, no provider calls). Offset pagination; new repos
 * enter the DB via useSyncCodebaseRepos().
 */
export const useCodebaseReposInfinite = (
  orgUUID: string,
  userUUID: string = '',
  options?: CodebaseReposFilters & {
    enabled?: boolean;
    pageSize?: number;
    staleTime?: number;
    /** Default false — modal reopen must not re-hit listCodebaseRepos. */
    refetchOnMount?: boolean | 'always';
  }
) => {
  const { orgResolutionDone } = useUserLogged();
  const pageSize = options?.pageSize ?? 50;
  const filters: CodebaseReposFilters = {
    search: options?.search ?? '',
    prBotOnly: options?.prBotOnly ?? false,
    prBotOrHasDocs: options?.prBotOrHasDocs ?? false,
    sortBy: options?.sortBy ?? 'name',
    gitlabGroupPath: options?.gitlabGroupPath ?? '',
  };

  const query = useInfiniteQuery({
    queryKey: codebaseReposQueryKeys.infinite(orgUUID, userUUID, filters),
    // pageParam stays a plain number (offset) — no union type, so TS flow
    // narrowing can't collapse the useInfiniteQuery generic inference.
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listCodebaseRepos(orgUUID, userUUID, {
        limit: pageSize,
        offset: pageParam,
        ...filters,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more
        ? allPages.reduce((n, p) => n + p.repositories.length, 0)
        : undefined,
    enabled: Boolean(orgUUID) && options?.enabled !== false,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: options?.refetchOnMount ?? false,
    retry: 2,
  });

  const firstPage = query.data?.pages?.[0];
  return {
    ...query,
    /**
     * Count an unresolved org as loading. The query is gated on orgUUID, and a
     * disabled TanStack query is pending but not fetching, so `query.isLoading`
     * is false with no rows yet and consumers paint their empty state, then a
     * skeleton once the org lands, then the data. Gated on `orgResolutionDone`
     * so a user who genuinely has no workspace still reaches the empty state
     * instead of an endless skeleton.
     */
    isLoading: query.isLoading || (!orgUUID && !orgResolutionDone),
    totalCount: firstPage?.total_count ?? 0,
    apiError: firstPage?.error,
  };
};

/** Options for one server-side page of the DB repo listing. */
export interface UseCodebaseReposPageOptions extends CodebaseReposFilters {
  pageIndex: number;
  pageSize: number;
  enabled?: boolean;
  staleTime?: number;
}

/**
 * One page of the DB repo listing with total_count/has_more, for table-style
 * pagination. Keeps previous data while switching pages and prefetches the
 * next page so "Next" feels instant.
 */
export const useCodebaseReposPage = (
  orgUUID: string,
  userUUID: string,
  options: UseCodebaseReposPageOptions
) => {
  const { orgResolutionDone } = useUserLogged();
  const {
    pageIndex,
    pageSize,
    enabled = true,
    staleTime = 5 * 60 * 1000,
  } = options;
  const filters: CodebaseReposFilters = {
    search: (options.search ?? '').trim(),
    prBotOnly: options.prBotOnly ?? false,
    prBotOrHasDocs: options.prBotOrHasDocs ?? false,
    sortBy: options.sortBy ?? 'name',
    gitlabGroupPath: options.gitlabGroupPath ?? '',
    gitOrg: options.gitOrg ?? '',
  };

  const queryClient = useQueryClient();

  const fetchPage = useCallback(
    (index: number) =>
      listCodebaseRepos(orgUUID, userUUID, {
        limit: pageSize,
        offset: index * pageSize,
        ...filters,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orgUUID,
      userUUID,
      pageSize,
      filters.search,
      filters.prBotOnly,
      filters.prBotOrHasDocs,
      filters.sortBy,
      filters.gitlabGroupPath,
      filters.gitOrg,
    ]
  );

  const query = useQuery({
    queryKey: codebaseReposQueryKeys.page(
      orgUUID,
      userUUID,
      pageIndex,
      pageSize,
      filters
    ),
    queryFn: () => fetchPage(pageIndex),
    enabled: Boolean(orgUUID) && enabled,
    staleTime,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (i) => Math.min(1000 * 2 ** i, 30000),
  });

  const has_more = query.data?.has_more ?? false;

  // Prefetch the next page so pagination feels instant. Offset pagination has
  // no cursor to poison, so this is safe even while showing placeholder data.
  useEffect(() => {
    if (!has_more || !orgUUID || !query.data || query.isPlaceholderData) {
      return;
    }
    const nextIndex = pageIndex + 1;
    queryClient.prefetchQuery({
      queryKey: codebaseReposQueryKeys.page(
        orgUUID,
        userUUID,
        nextIndex,
        pageSize,
        filters
      ),
      queryFn: () => fetchPage(nextIndex),
      staleTime,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryClient,
    orgUUID,
    userUUID,
    pageIndex,
    pageSize,
    has_more,
    query.data,
    query.isPlaceholderData,
    staleTime,
    fetchPage,
  ]);

  return {
    repositories: (query.data?.repositories ?? []) as UnifiedRepository[],
    total_count: query.data?.total_count ?? 0,
    has_more,
    /** Org-level agent-insights default for repos with no explicit override. */
    agent_insights_org_default:
      query.data?.agent_insights_org_default ?? true,
    /** Backend "Error" field from the API response */
    apiError: query.data?.error,
    /**
     * Count an unresolved org as loading. The query is gated on orgUUID, and a
     * disabled TanStack query is pending but not fetching, so `query.isLoading`
     * is false with no rows yet and consumers paint their empty state ("No
     * repositories found"), then a skeleton once the org lands, then the data.
     * Gated on `orgResolutionDone` so a user who genuinely has no workspace
     * still reaches the empty state instead of an endless skeleton.
     */
    isLoading: query.isLoading || (!orgUUID && !orgResolutionDone),
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};

export interface UseSyncCodebaseReposOptions {
  /**
   * 'all' (default): success + error toasts — for manual sync buttons.
   * 'error-only': no success toast — for auto-syncs (onboarding, Add Repo
   * return) whose UI already shows completion.
   */
  toasts?: 'all' | 'error-only';
}

/**
 * Trigger the explicit provider→DB repo sync (SSE) with live progress state.
 *
 * The only door provider data enters through. On completion invalidates BOTH
 * repo-listing query-key roots (new + legacy, during the migration) so every
 * surface refetches. Consumed by SyncReposButton and onboarding sync gates.
 */
export const useSyncCodebaseRepos = (options?: UseSyncCodebaseReposOptions) => {
  const toasts = options?.toasts ?? 'all';
  const queryClient = useQueryClient();
  const toast = useToastManager();
  const [isSyncing, setIsSyncing] = useState(false);
  /** Live progress from SSE ticks: repos scanned in the provider listing
   * (falls back to DB total on old backends). Null until the first tick. */
  const [liveCount, setLiveCount] = useState<number | null>(null);
  // In-flight guard that survives re-renders without retriggering callbacks.
  const inFlightRef = useRef(false);

  const sync = useCallback(
    async (orgUUID: string, userUUID: string = '') => {
      if (!orgUUID || inFlightRef.current) return null;
      inFlightRef.current = true;
      setIsSyncing(true);
      setLiveCount(null);
      try {
        const result = await syncCodebaseRepos(orgUUID, userUUID, (e) =>
          setLiveCount(e.scanned ?? e.total_repos)
        );
        if (result.status === 'error') {
          toast.show({
            title: 'Sync failed',
            description:
              "Couldn't sync repositories. Please try again in a moment.",
            status: 'error',
          });
        } else {
          await queryClient.invalidateQueries({
            queryKey: codebaseReposQueryKeys.all,
          });
          await queryClient.invalidateQueries({
            queryKey: unifiedCodebaseQueryKeys.all,
          });
          if (toasts === 'all') {
            toast.show({
              title: 'Sync complete',
              description:
                result.added > 0
                  ? `${result.added} new ${result.added === 1 ? 'repository' : 'repositories'} added.`
                  : 'Everything is up to date.',
              status: 'success',
            });
          }
        }
        return result;
      } catch (error) {
        console.error('Repo sync failed', error);
        toast.show({
          title: 'Sync failed',
          description:
            "Couldn't sync repositories. Please try again in a moment.",
          status: 'error',
        });
        return null;
      } finally {
        inFlightRef.current = false;
        setIsSyncing(false);
        setLiveCount(null);
      }
    },
    [queryClient, toast, toasts]
  );

  return { sync, isSyncing, liveCount };
};
